/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import * as React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import {
  ArrowRight,
  CircleAlert,
  HardDrive,
  Info,
  LoaderCircle,
  Monitor,
} from "lucide-react";

import fieldHelper from "@src/@types/Field";
import { ActionItem } from "@src/@types/MainItem";
import { MinionPool } from "@src/@types/MinionPool";
import EndpointLogos from "@src/components/modules/EndpointModule/EndpointLogos";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import TransferDetailsTable from "@src/components/modules/TransferModule/TransferDetailsTable";
import {
  scenarioLabel,
  transferScenario,
} from "@src/components/modules/TransferModule/TransferListItem/TransferListItem";
import CopyValue from "@src/components/ui/CopyValue";
import PasswordValue from "@src/components/ui/PasswordValue";
import {
  DetailsColumns,
  DetailsPage,
  KeyValueGrid,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import type { KvItem } from "@src/components/ui/DetailsKit/DetailsKit";
import { T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import LabelDictionary from "@src/utils/LabelDictionary";
import configLoader from "@src/utils/Config";

import type { Instance } from "@src/@types/Instance";
import type { Endpoint, StorageBackend } from "@src/@types/Endpoint";
import type { Network } from "@src/@types/Network";
import type { Field as FieldType } from "@src/@types/Field";

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--t9-danger-200);
  border-radius: 8px;
  background: var(--t9-danger-50);
  color: var(--t9-danger-700);
  font-size: 14px;
  line-height: 20px;
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;
const ValueLink = styled(Link)`
  color: ${T9.brand600};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const Missing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--t9-danger-700);
`;
const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 160px;
  color: ${T9.slate500};
  font-size: 14px;
  svg {
    animation: md-spin 1s linear infinite;
  }
  @keyframes md-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Spinner = styled(LoaderCircle)`
  color: ${T9.slate400};
  animation: md-spin 1s linear infinite;
  @keyframes md-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Route = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  svg.arrow {
    color: ${T9.slate400};
  }
`;
const SideLabel = styled.div`
  font-size: 12px;
  color: ${T9.slate500};
  margin-bottom: 4px;
`;
const OptionsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 18px 0 10px 0;
  padding-top: 14px;
  border-top: 1px solid ${T9.slate100};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${T9.slate500};
`;

type Props = {
  item?: ActionItem | null;
  minionPools: MinionPool[];
  storageBackends: StorageBackend[];
  destinationSchema: FieldType[];
  destinationSchemaLoading: boolean;
  sourceSchema: FieldType[];
  sourceSchemaLoading: boolean;
  instancesDetails: Instance[];
  instancesDetailsLoading: boolean;
  endpoints: Endpoint[];
  networks?: Network[];
  bottomControls: React.ReactNode;
  loading: boolean;
};
type State = {
  showPassword: string[];
};
@observer
class MainDetails extends React.Component<Props, State> {
  state = {
    showPassword: [],
  };

  getSourceEndpoint(): Endpoint | undefined {
    const endpoint = this.props.endpoints.find(
      e => this.props.item && e.id === this.props.item.origin_endpoint_id,
    );
    return endpoint;
  }

  getDestinationEndpoint(): Endpoint | undefined {
    const endpoint = this.props.endpoints.find(
      e => this.props.item && e.id === this.props.item.destination_endpoint_id,
    );
    return endpoint;
  }

  renderValue(value: string) {
    return <CopyValue value={value} maxWidth="100%" />;
  }

  renderEndpointLink(type: string): React.ReactNode {
    const endpoint =
      type === "source"
        ? this.getSourceEndpoint()
        : this.getDestinationEndpoint();

    if (endpoint) {
      return (
        <ValueLink to={`/endpoints/${endpoint.id}`}>{endpoint.name}</ValueLink>
      );
    }

    return (
      <Missing>
        <CircleAlert size={14} />
        Endpoint is missing
      </Missing>
    );
  }

  renderPropertiesTable(
    propertyNames: string[],
    type: "source" | "destination",
  ) {
    const endpoint =
      type === "source"
        ? this.getSourceEndpoint()
        : this.getDestinationEndpoint();

    const getValue = (name: string, value: any) => {
      if (
        value.join &&
        value.length &&
        value[0].destination &&
        value[0].source
      ) {
        return value
          .map(
            (v: { source: any; destination: any }) =>
              `${v.source}=${v.destination}`,
          )
          .join(", ");
      }
      const schema =
        type === "source"
          ? this.props.sourceSchema
          : this.props.destinationSchema;
      return fieldHelper.getValueAlias({
        name,
        value,
        fields: schema,
        targetProvider: endpoint && endpoint.type,
      });
    };

    let properties: any[] = [];
    let dictionaryKey = "";
    if (endpoint) {
      dictionaryKey = `${endpoint.type}-${type}`;
    }
    const environment =
      this.props.item &&
      (type === "source"
        ? this.props.item.source_environment
        : this.props.item.destination_environment);
    propertyNames.forEach(pn => {
      const value = environment ? environment[pn] : "";
      const label = LabelDictionary.get(pn, dictionaryKey);

      if (value && value.join) {
        value.forEach((v: any, i: number) => {
          const useLabel = i === 0 ? label : "";
          properties.push({ label: useLabel, value: v, name: pn });
        });
      } else if (value && typeof value === "object") {
        properties = properties.concat(
          Object.keys(value).map(p => {
            if (p === "disk_mappings" || p === "backend_mappings") {
              return null;
            }
            if (value[p] == null || value[p] == undefined) {
              return null;
            }
            return {
              label: `${label} - ${LabelDictionary.get(p)}`,
              value: getValue(p, value[p]),
              name: p,
            };
          }),
        );
      } else {
        properties.push({ label, value: getValue(pn, value), name: pn });
      }
    });

    const items: KvItem[] = properties
      .filter(Boolean)
      .filter(p => p.value != null && p.value !== "")
      .map((prop, i) => ({
        label: prop.label || `${properties[i - 1]?.label || ""}`,
        value:
          (String(prop.label).toLowerCase().indexOf("password") > -1 ||
            configLoader.config.passwordFields.find(f => f === prop.name)) &&
          !this.state.showPassword.find(f => f === `${prop.label}-${type}`) ? (
            <PasswordValue
              value={prop.value}
              onShow={() =>
                this.setState(prevState => ({
                  showPassword: [
                    ...prevState.showPassword,
                    `${prop.label}-${type}`,
                  ],
                }))
              }
            />
          ) : (
            <CopyValue value={String(prop.value)} maxWidth="100%" />
          ),
      }));
    return <KeyValueGrid items={items} columns={2} />;
  }

  renderSide(type: "source" | "destination", propertyNames: string[]) {
    const item = this.props.item;
    const endpoint =
      type === "source" ? this.getSourceEndpoint() : this.getDestinationEndpoint();
    const poolId =
      type === "source" ? item?.origin_minion_pool_id : item?.destination_minion_pool_id;
    const pool = this.props.minionPools.find(m => m.id === poolId);
    const schemaLoading =
      type === "source" ? this.props.sourceSchemaLoading : this.props.destinationSchemaLoading;
    return (
      <SectionCard
        title={type === "source" ? "Source" : "Target"}
        subtitle={endpoint ? providerLabel(endpoint.type) : undefined}
        actions={endpoint ? <EndpointLogos endpoint={endpoint.type as any} height={32} /> : null}
        testId={`mainDetails-${type}`}
      >
        <KeyValueGrid
          columns={2}
          items={[
            { label: "Endpoint", value: this.renderEndpointLink(type === "source" ? "source" : "target") },
            {
              label: type === "source" ? "Source SwiftIQ Agent" : "Target SwiftIQ Agent",
              hidden: !poolId,
              value: pool ? (
                <ValueLink to={`/minion-pools/${pool.id}`}>{pool.name}</ValueLink>
              ) : (
                poolId
              ),
            },
          ]}
        />
        {propertyNames.length > 0 ? (
          <>
            <OptionsTitle>
              {type === "source" ? "Source options" : "Target options"}
              {schemaLoading ? <Spinner size={12} /> : null}
            </OptionsTitle>
            {this.renderPropertiesTable(propertyNames, type)}
          </>
        ) : null}
      </SectionCard>
    );
  }

  renderOverview() {
    const item = this.props.item;
    if (!item) return null;
    const sourceEndpoint = this.getSourceEndpoint();
    const destinationEndpoint = this.getDestinationEndpoint();
    const isDeployment = item.type === "deployment";
    return (
      <SectionCard title="Overview" icon={Info} testId="mainDetails-overview">
        <Route style={{ marginBottom: 18 }}>
          <div>
            <SideLabel>Source</SideLabel>
            <EndpointLogos endpoint={(sourceEndpoint?.type || "") as any} height={42} />
          </div>
          <ArrowRight className="arrow" size={20} />
          <div>
            <SideLabel>Target</SideLabel>
            <EndpointLogos endpoint={(destinationEndpoint?.type || "") as any} height={42} />
          </div>
        </Route>
        <KeyValueGrid
          columns={4}
          items={[
            { label: "Type", value: isDeployment ? "Deployment" : scenarioLabel(transferScenario(item)) },
            { label: "Instances", value: String(item.instances.length) },
            {
              label: "Created",
              value: item.created_at ? (
                <span title={formatDateTime(item.created_at)}>{formatDateTime(item.created_at)}</span>
              ) : null,
            },
            {
              label: "Last updated",
              value: item.updated_at ? (
                <span title={formatDateTime(item.updated_at)}>
                  {formatDateTime(item.updated_at)} · {timeAgo(item.updated_at)}
                </span>
              ) : null,
            },
            { label: "ID", value: this.renderValue(item.id || "-"), mono: true, span: 2 },
            {
              label: "Created from transfer",
              span: 2,
              hidden: !(item.type === "deployment" && item.transfer_id),
              value:
                item.type === "deployment" ? (
                  <ValueLink to={`/transfers/${item.transfer_id}`}>{item.transfer_id}</ValueLink>
                ) : null,
            },
            {
              label: "Description",
              span: 4,
              hidden: !item.description,
              value: item.description,
            },
          ]}
        />
      </SectionCard>
    );
  }

  renderTable() {
    if (this.props.loading || !this.props.item) {
      return null;
    }

    const getPropertyNames = (type: "source" | "destination") => {
      const env =
        this.props.item &&
        (type === "source"
          ? this.props.item.source_environment
          : this.props.item.destination_environment);
      return env
        ? Object.keys(env).filter(
            k =>
              k !== "network_map" &&
              (k !== "storage_mappings" ||
                (env[k] != null &&
                  typeof env[k] === "object" &&
                  Object.keys(env[k]).length > 0)),
          )
        : [];
    };

    return (
      <>
        {this.renderOverview()}
        <DetailsColumns>
          {this.renderSide("source", getPropertyNames("source"))}
          {this.renderSide("destination", getPropertyNames("destination"))}
        </DetailsColumns>
      </>
    );
  }

  renderBottomControls() {
    if (this.props.loading) {
      return null;
    }

    return this.props.bottomControls;
  }

  renderLoading() {
    if (!this.props.loading && !this.props.instancesDetailsLoading) {
      return null;
    }

    return (
      <Loading>
        <LoaderCircle size={20} />
        Loading…
      </Loading>
    );
  }

  renderSpecialError() {
    if (this.props.item?.last_execution_status !== "ERROR_ALLOCATING_MINIONS") {
      return null;
    }

    return (
      <Banner>
        <CircleAlert size={16} />
        <div>
          SwiftIQ could not allocate agent machines for this {this.props.item.type}. Check the
          events of the SwiftIQ Agent(s) it uses, and the logs of the SwiftIQ agent manager, for the
          details.
        </div>
      </Banner>
    );
  }

  render() {
    return (
      <DetailsPage>
        {this.renderSpecialError()}
        {this.renderTable()}
        {this.props.instancesDetailsLoading || this.props.loading ? null : (
          <SectionCard
            title="Instances, networks and storage"
            icon={Monitor}
            subtitle="How each source machine maps onto the target"
            actions={<HardDrive size={16} color={T9.slate400} />}
            testId="mainDetails-instances"
          >
            <TransferDetailsTable
              item={this.props.item}
              minionPools={this.props.minionPools}
              instancesDetails={this.props.instancesDetails}
              networks={this.props.networks}
              storageBackends={this.props.storageBackends}
            />
          </SectionCard>
        )}
        {this.renderLoading()}
        {this.renderBottomControls()}
      </DetailsPage>
    );
  }
}

export default MainDetails;
